import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useFileRecordDownload } from "@/hooks/useFileRecordDownload";
import type { RailRowMenuController } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailRowContextMenu";
import {
  ObjectActionMenu,
  attachFileRecordDownloadVariants,
  createRemoteFileRecordExportRunner,
  useFileRecordObjectActionHandlers,
  useObjectActions,
  type ActionScope,
  type ActionSurface,
} from "@/object-actions";
import { cn } from "@/utils";
import { RESOURCE_TAG_FAVORITE, type FileRecord } from "@cohesive/api-client";
import { MoreHorizontal } from "lucide-react";
import { useMemo, useState } from "react";

export type WorkspaceFileRecordMenuSlotProps = {
  record: FileRecord;
  onOpen?: (record: FileRecord) => void;
  onRename?: (record: FileRecord) => void;
  onMove?: (record: FileRecord) => void;
  onCopy?: (record: FileRecord) => void;
  onDelete?: (record: FileRecord) => void;
  /** Registry scope for manifest predicates (favorites, shared-with-me, etc.). */
  actionScope?: ActionScope;
  /** Override hover/open visibility — e.g. `group-hover/row:opacity-100` in the left rail. */
  menuVisibilityClassName?: string;
  onMenuOpenChange?: (open: boolean) => void;
  /**
   * Controlled open state. When provided, the parent owns the menu's open state
   * (e.g. the rail row opening it on right-click); otherwise it's internal.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * Rail context-menu controller (from `useRailRowContextMenu`). When provided
   * with `railTrigger`, the menu renders through the shared `RailRowMenu` so it
   * opens at the cursor on right-click and below the kebab on click.
   */
  menuController?: RailRowMenuController;
  /** Left-rail row menus — compact `size-6` trigger matching canvas/workspace rows. */
  railTrigger?: boolean;
  /** Render menu items only — parent owns the dropdown shell (e.g. list-view RowKebab). */
  itemsOnly?: boolean;
};

export function WorkspaceFileRecordMenuSlot({
  record,
  onOpen,
  onRename,
  onMove,
  onCopy,
  onDelete,
  actionScope,
  menuVisibilityClassName,
  onMenuOpenChange,
  open: openProp,
  onOpenChange,
  menuController,
  railTrigger,
  itemsOnly = false,
}: WorkspaceFileRecordMenuSlotProps) {
  const [internalMenuOpen, setInternalMenuOpen] = useState(false);
  const isMenuOpen = openProp ?? internalMenuOpen;
  const menuOpen = menuController?.open ?? isMenuOpen;

  const handleMenuOpenChange = (open: boolean) => {
    if (openProp === undefined) setInternalMenuOpen(open);
    onOpenChange?.(open);
    onMenuOpenChange?.(open);
  };

  const fileName = record.path?.split("/").filter(Boolean).pop() || "File";
  const isFolder = record.path?.endsWith("/") ?? false;
  const isFavorite = !!record.tags?.includes(RESOURCE_TAG_FAVORITE);
  const canShare = record.can_edit !== false;
  const scope: ActionScope = actionScope ?? "canonical";
  const surface: ActionSurface = railTrigger ? "left-rail" : "main-view";

  const handlerDeps = useMemo(
    () => ({
      onOpen,
      onRename,
      onMove,
      onCopy,
      onDelete,
      actionScope: scope,
    }),
    [onCopy, onDelete, onMove, onOpen, onRename, scope],
  );

  const handlers = useFileRecordObjectActionHandlers(record, handlerDeps);

  const { actions: resolvedActions } = useObjectActions({
    kind: {
      type: "file-record",
      class: record.class,
      isFolder,
    },
    surface,
    scope,
    enabled: itemsOnly ? isMenuOpen : menuOpen,
    state: {
      isFavorite,
      canShare,
      canEdit: record.can_edit !== false,
      canDelete: !!onDelete,
    },
    handlers,
  });

  const { download } = useFileRecordDownload();
  const exportRunner = useMemo(
    () => createRemoteFileRecordExportRunner(record, download),
    [download, record],
  );

  const actions = useMemo(
    () =>
      attachFileRecordDownloadVariants(resolvedActions, {
        record,
        runExport: exportRunner.runExport,
        isVariantDisabled: exportRunner.isVariantDisabled,
      }),
    [exportRunner, record, resolvedActions],
  );

  const menuItems = (
    <ObjectActionMenu
      actions={actions}
      kind={{ type: "file-record", class: record.class, isFolder }}
      name={fileName}
      menuController={railTrigger ? menuController : undefined}
      label={railTrigger ? fileName : undefined}
    />
  );

  if (itemsOnly) {
    return menuItems;
  }

  if (railTrigger && menuController) {
    return menuItems;
  }

  return (
    <div
      className={cn(
        "transition-opacity duration-150",
        menuVisibilityClassName ??
          (isMenuOpen ? "opacity-100" : "opacity-0 group-hover:opacity-100"),
      )}
    >
      <DropdownMenu
        modal={false}
        open={isMenuOpen}
        onOpenChange={handleMenuOpenChange}
      >
        <DropdownMenuTrigger asChild>
          {railTrigger ? (
            <Button
              type="button"
              variant="ghost"
              size="xs"
              className="size-6 p-0"
              aria-label={`${fileName} actions`}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal className="size-4" />
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal />
            </Button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align={railTrigger ? "start" : "end"}
          alignOffset={0}
          side="bottom"
          sideOffset={4}
          className="w-44"
        >
          {menuItems}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
